import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExportIcon from '@mui/icons-material/FileDownloadOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import complianceReportService, {
  type AdminLicenseComplianceDto,
  type TenantComplianceReportDto,
  type UserComplianceSummaryDto,
} from '../../services/complianceReportService';
import ProvenanceBadge from '../../components/compliance/ProvenanceBadge';
import MemberComplianceDrawer from '../../components/compliance/MemberComplianceDrawer';
import DisclaimerNote from '../../components/compliance/DisclaimerNote';
import FirmOverview, { type StatusFilter } from '../../components/compliance/FirmOverview';
import { FULFILLMENT_DISCLAIMER } from '../../components/compliance/disclaimers';
import {
  getMemberBucket,
  isLicenseEthicsShort,
  type MemberBucket,
} from '../../components/compliance/firmRollup';
import {
  getLicenseStanding,
  isAdminLicenseAtRisk,
  isLicenseCompliant,
  isLicenseSetUp,
} from '../../components/compliance/licenseStatus';
import { getUrgency } from '../../components/compliance/urgency';
import { getApiErrorInfo } from '../../services/api';
import { useUserContext } from '../../hooks/useUserContext';
import { track } from '../../services/analytics';

const LICENSE_LINE_HEIGHT = 30;

const STATUS_FILTER_VALUES: StatusFilter[] = [
  'all',
  'attention',
  'at_risk',
  'in_progress',
  'concluded',
  'not_set_up',
];

/** Ascending sort order for the Status column: worst standing first. */
const BUCKET_SEVERITY: Record<MemberBucket, number> = {
  attention: 0,
  at_risk: 1,
  in_progress: 2,
  not_set_up: 3,
  concluded: 4,
};

/** Display labels shared with the FirmOverview legend vocabulary. */
const BUCKET_LABELS: Record<MemberBucket, string> = {
  attention: 'Attention required',
  at_risk: 'At risk',
  in_progress: 'In progress',
  concluded: 'Compliant',
  not_set_up: 'Not set up',
};

/** Maps member buckets to theme `status.*` token names for chip colors.
 *  In progress is blue (`onPace`), never green: on this page green appears
 *  once and always means compliant. */
const BUCKET_TOKENS: Record<MemberBucket, string> = {
  attention: 'attention',
  at_risk: 'atRisk',
  in_progress: 'onPace',
  concluded: 'completed',
  not_set_up: 'enrolled',
};

/** "2027-01" → "Jan '27", matching the overview band's month labels. */
function deadlineMonthLabel(key: string): string {
  const date = new Date(`${key}-01T00:00:00Z`);
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} '${String(date.getUTCFullYear()).slice(2)}`;
}

export default function ComplianceReport() {
  const { backendProfile } = useUserContext();
  const theme = useTheme();
  // Below sm the 720px table clips badly; swap in a card-per-member list.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [report, setReport] = useState<TenantComplianceReportDto | null>(null);
  // One clock per report load: the band, table urgency lines, sort, and CSV all
  // judge "days left" against the same instant, so their counts always agree.
  const [now, setNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hub "At a Glance" rows deep-link here with ?status=attention / at_risk.
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') as StatusFilter | null;

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    initialStatus && STATUS_FILTER_VALUES.includes(initialStatus) ? initialStatus : 'all'
  );
  const [deadlineMonthFilter, setDeadlineMonthFilter] = useState<string | null>(null);
  const [ethicsFilter, setEthicsFilter] = useState(false);
  const [sortBy, setSortBy] = useState<'status' | 'time' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [drawerUser, setDrawerUser] = useState<UserComplianceSummaryDto | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadReport = useCallback(async () => {
    if (!backendProfile?.tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await complianceReportService.getComplianceReport(backendProfile.tenantId);
      setReport(data);
      setNow(new Date());
      track('compliance_report_run');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to load compliance report.');
    } finally {
      setLoading(false);
    }
  }, [backendProfile?.tenantId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Exclusive standing bucket per member; shared vocabulary with the
  // FirmOverview band so segment counts match table rows. When a state filter
  // is active, buckets derive from that jurisdiction's licenses only, so
  // "TX + Attention Required" can't be triggered by another state's license.
  const memberBuckets = useMemo(() => {
    const stateScope = stateFilter === 'all' ? undefined : stateFilter;
    const map = new Map<string, MemberBucket>();
    for (const user of report?.users ?? []) {
      map.set(user.userId, getMemberBucket(user, now, stateScope));
    }
    return map;
  }, [report, now, stateFilter]);

  // Sorted union of license state codes across members
  const stateOptions = useMemo(() => {
    const states = new Set<string>();
    for (const user of report?.users ?? []) {
      for (const license of user.licenses ?? []) states.add(license.stateCode);
    }
    return [...states].sort();
  }, [report]);

  // Licenses considered by the month/ethics filters; scoped to the selected
  // state so all filter dimensions agree on which licenses they judge.
  const scopedLicenses = useCallback(
    (user: UserComplianceSummaryDto) =>
      stateFilter === 'all'
        ? (user.licenses ?? [])
        : (user.licenses ?? []).filter((l) => l.stateCode === stateFilter),
    [stateFilter]
  );

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = (report?.users ?? []).filter((user) => {
      if (
        term &&
        !user.fullName.toLowerCase().includes(term) &&
        !user.email.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (stateFilter !== 'all' && !(user.licenses ?? []).some((l) => l.stateCode === stateFilter)) {
        return false;
      }
      if (statusFilter !== 'all' && memberBuckets.get(user.userId) !== statusFilter) {
        return false;
      }
      if (
        deadlineMonthFilter &&
        !scopedLicenses(user).some(
          (l) => isLicenseSetUp(l) && l.periodEnd?.slice(0, 7) === deadlineMonthFilter
        )
      ) {
        return false;
      }
      if (ethicsFilter && !scopedLicenses(user).some(isLicenseEthicsShort)) {
        return false;
      }
      return true;
    });
    if (!sortBy) return matched;
    const direction = sortDir === 'asc' ? 1 : -1;
    const sortKey = (user: UserComplianceSummaryDto): number => {
      if (sortBy === 'status') {
        return BUCKET_SEVERITY[memberBuckets.get(user.userId) ?? 'not_set_up'];
      }
      // Time to report: fewest days left across set-up licenses, worst first.
      let min = Number.POSITIVE_INFINITY;
      for (const license of scopedLicenses(user)) {
        if (!isLicenseSetUp(license) || !license.periodEnd) continue;
        const { daysLeft } = getUrgency(
          license.periodEnd,
          isLicenseCompliant(license, user.licenses),
          now
        );
        if (daysLeft < min) min = daysLeft;
      }
      return min;
    };
    return [...matched].sort(
      (a, b) => (sortKey(a) - sortKey(b)) * direction || a.fullName.localeCompare(b.fullName)
    );
  }, [
    report,
    search,
    stateFilter,
    statusFilter,
    deadlineMonthFilter,
    ethicsFilter,
    memberBuckets,
    scopedLicenses,
    sortBy,
    sortDir,
    now,
  ]);

  const filtersActive =
    search.trim() !== '' ||
    stateFilter !== 'all' ||
    statusFilter !== 'all' ||
    deadlineMonthFilter !== null ||
    ethicsFilter;

  const clearFilters = () => {
    setSearch('');
    setStateFilter('all');
    setStatusFilter('all');
    setDeadlineMonthFilter(null);
    setEthicsFilter(false);
  };

  const handleSort = (column: 'status' | 'time') => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const handleExportCsv = () => {
    if (!report) return;
    const rows: string[][] = [
      [
        'Name',
        'Email',
        'State',
        'Status',
        'At Risk',
        'Requirements',
        'Earned Hours',
        'Target Hours',
        'Ethics Earned',
        'Ethics Target',
        'Period Start',
        'Period End',
        'Days Left',
      ],
    ];
    // Export honors the active filters — the overview band teaches "narrow to
    // who needs action", so the CSV must match what the table shows.
    for (const user of filteredUsers) {
      const licenses = user.licenses ?? [];
      if (licenses.length === 0) {
        rows.push([user.fullName, user.email, '', '', '', '', '', '', '', '', '', '', '']);
        continue;
      }
      for (const license of licenses) {
        const setUp = isLicenseSetUp(license);
        // Derived (reciprocity) rows carry no local total hours — blank cells
        // beat inventing zeros; their ethics cells still print for carve-out states.
        const derived = license.complianceMode === 'home_state_reciprocity';
        const urgency =
          setUp && license.periodEnd
            ? getUrgency(license.periodEnd, isLicenseCompliant(license, licenses), now)
            : null;
        rows.push([
          user.fullName,
          user.email,
          license.stateCode,
          standingLabel(license, licenses, now),
          isAdminLicenseAtRisk(license, now, licenses) ? 'Yes' : '',
          derived ? 'Home-state reciprocity' : confirmStatusLabel(license.status),
          setUp && !derived ? String(license.earnedHours ?? 0) : '',
          setUp && !derived && license.targetHours != null ? String(license.targetHours) : '',
          setUp && (!derived || license.ethicsTargetHours != null)
            ? String(license.ethicsEarnedHours ?? 0)
            : '',
          setUp && license.ethicsTargetHours != null ? String(license.ethicsTargetHours) : '',
          license.periodStart ? license.periodStart.slice(0, 10) : '',
          license.periodEnd ? license.periodEnd.slice(0, 10) : '',
          urgency ? (urgency.periodEnded ? 'Period ended' : String(urgency.daysLeft)) : '',
        ]);
      }
    }
    const csv = rows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const isFiltered = filteredUsers.length !== report.users.length;
    link.download = `team-cpe-report${isFiltered ? '-filtered' : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    track('compliance_report_csv_export');
  };

  const openDrawer = (user: UserComplianceSummaryDto) => {
    setDrawerUser(user);
    setDrawerOpen(true);
  };

  return (
    // position: relative keeps the visually-hidden live region (absolute)
    // contained in this page's flow; without it the region hangs off the
    // document root and makes the window scrollable past the footer.
    <Box sx={{ position: 'relative' }}>
      {/* Page header — title only; the report below explains itself */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h4" component="h1">
          Team Reporting
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ExportIcon />}
          onClick={handleExportCsv}
          disabled={!report || loading}
        >
          Export CSV
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadReport}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        // Skeleton mirrors the loaded layout (one container: overview band,
        // tinted search band, roster) so the page doesn't jump when data arrives.
        <Box aria-label="Loading compliance report" role="status">
          <Card variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2.5 }}>
              <Skeleton variant="rounded" height={210} />
            </Box>
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'surface.subtle',
                px: 2.5,
                py: 1.75,
              }}
            >
              <Skeleton variant="rounded" height={40} />
            </Box>
            <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={44} sx={{ mb: 1 }} />
              ))}
            </Box>
          </Card>
        </Box>
      ) : (
        report && (
          <>
            {/* One report object: rollup band, tinted search band, filter
                chips, then the roster. A single border wraps all of it (same
                contract as the member Reporting page's credit ledger) so the
                band visibly scopes the table its panels filter. */}
            <Card variant="outlined" sx={{ overflow: 'hidden' }}>
              {/* Firm-wide rollup visuals; the standing bar drives the status filter */}
              <FirmOverview
                users={report.users}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                stateFilter={stateFilter}
                onStateFilterChange={setStateFilter}
                deadlineMonthFilter={deadlineMonthFilter}
                onDeadlineMonthFilterChange={setDeadlineMonthFilter}
                ethicsFilter={ethicsFilter}
                onEthicsFilterChange={setEthicsFilter}
                now={now}
              />

              {/* Search + state select as a tinted band: the seam between the
                  overview above and the roster below, scoping both. (No top
                  hairline when the overview is absent — the card edge is enough.) */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  borderTop: report.users.length > 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: 'surface.subtle',
                  px: 2.5,
                  py: 1.75,
                }}
              >
                <TextField
                  placeholder="Search by name or email"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    flexGrow: 1,
                    minWidth: 220,
                    '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  sx={{
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                  }}
                  slotProps={{ select: { inputProps: { 'aria-label': 'Filter by state' } } }}
                >
                  <MenuItem value="all">All states</MenuItem>
                  {stateOptions.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Every active filter stays visible in a thin band directly
                  above the roster it narrows, including the band-only ones
                  (deadline month, ethics gap): a silently filtered roster
                  must never pass for the full one. */}
              {filtersActive && (
                <Box
                  role="group"
                  aria-label="Active filters"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 2.5,
                    py: 1.25,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {statusFilter !== 'all' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Status: ${BUCKET_LABELS[statusFilter]}`}
                      onDelete={() => setStatusFilter('all')}
                    />
                  )}
                  {stateFilter !== 'all' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`State: ${stateFilter}`}
                      onDelete={() => setStateFilter('all')}
                    />
                  )}
                  {deadlineMonthFilter && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Deadline: ${deadlineMonthLabel(deadlineMonthFilter)}`}
                      onDelete={() => setDeadlineMonthFilter(null)}
                    />
                  )}
                  {ethicsFilter && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Ethics gap"
                      onDelete={() => setEthicsFilter(false)}
                    />
                  )}
                  <Button size="small" onClick={clearFilters} sx={{ ml: 'auto' }}>
                    Clear all
                  </Button>
                </Box>
              )}

              {isMobile ? (
                <Box>
                  {filteredUsers.map((user) => (
                    <MobileMemberCard
                      key={user.userId}
                      user={user}
                      bucket={memberBuckets.get(user.userId) ?? 'not_set_up'}
                      now={now}
                      onOpen={() => openDrawer(user)}
                    />
                  ))}
                  {filteredUsers.length === 0 && (
                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 6, px: 2.5, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        {(report.users.length ?? 0) === 0
                          ? 'No users found in this organization.'
                          : 'No members match your filters.'}
                      </Typography>
                      {report.users.length > 0 && filtersActive && (
                        <Button size="small" onClick={clearFilters} sx={{ mt: 1.5 }}>
                          Clear filters
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
              <TableContainer sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team member</TableCell>
                      <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortDir : 'asc'}
                          onClick={() => handleSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'time' ? sortDir : false} sx={{ whiteSpace: 'nowrap' }}>
                        <TableSortLabel
                          active={sortBy === 'time'}
                          direction={sortBy === 'time' ? sortDir : 'asc'}
                          onClick={() => handleSort('time')}
                        >
                          Time to Report
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow
                        key={user.userId}
                        hover
                        onClick={() => openDrawer(user)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', pt: 2.25 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{user.fullName}</Typography>
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{user.email}</Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', pt: 2.25 }}>
                          {(user.licenses ?? []).length === 0 ? (
                            <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {user.licenses.map((license) => (
                                <LicenseStatusLine
                                  key={license.licenseId}
                                  license={license}
                                  memberLicenses={user.licenses}
                                  now={now}
                                />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', pt: 2.25 }}>
                          {(user.licenses ?? []).length === 0 ? (
                            <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {user.licenses.map((license) => (
                                <TimeToReportLine
                                  key={license.licenseId}
                                  license={license}
                                  memberLicenses={user.licenses}
                                  now={now}
                                />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'top', pt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ChevronRightIcon fontSize="small" />}
                            onClick={(e) => {
                              // Row click opens the drawer too; don't double-fire.
                              e.stopPropagation();
                              openDrawer(user);
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            View details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography color="text.secondary">
                            {(report.users.length ?? 0) === 0
                              ? 'No users found in this organization.'
                              : 'No members match your filters.'}
                          </Typography>
                          {report.users.length > 0 && filtersActive && (
                            <Button size="small" onClick={clearFilters} sx={{ mt: 1.5 }}>
                              Clear filters
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Card>

            {/* Announces filter changes to screen readers (the table itself refilters silently). */}
            <Box aria-live="polite" sx={visuallyHidden}>
              Showing {filteredUsers.length} of {report.users.length} members
            </Box>

            <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1.5, display: 'block' }}>
              Showing {filteredUsers.length} of {report.users.length} member
              {report.users.length !== 1 ? 's' : ''} · Generated{' '}
              {new Date(report.generatedAt).toLocaleString()} · Figures reflect each license's
              confirmed reporting period.
            </Typography>

            {/* Informational disclaimer lives in the footer fine-print zone so it
                doesn't interrupt the overview-to-roster scan path. */}
            <DisclaimerNote sx={{ mt: 1.5 }}>{FULFILLMENT_DISCLAIMER}</DisclaimerNote>
          </>
        )
      )}

      <MemberComplianceDrawer
        open={drawerOpen}
        tenantId={backendProfile?.tenantId ?? null}
        user={drawerUser}
        report={report}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function standingLabel(
  license: AdminLicenseComplianceDto,
  memberLicenses?: AdminLicenseComplianceDto[],
  now?: Date
): string {
  switch (getLicenseStanding(license, now, memberLicenses)) {
    case 'concluded':
      return 'Compliant';
    case 'attention':
      return 'Attention required';
    case 'in_progress':
      return 'In progress';
    default:
      // Match the UI's ProvenanceBadge wording for licenses that aren't set up.
      return license.status === 'unconfirmed' ? 'Unconfirmed' : 'Not set up';
  }
}

/** CSV label for a license's requirement confirm status — mirrors ProvenanceBadge wording. */
function confirmStatusLabel(status: AdminLicenseComplianceDto['status']): string {
  switch (status) {
    case 'confirmed':
      return 'Defaults confirmed';
    case 'modified':
      return 'Custom requirements';
    case 'unconfirmed':
      return 'Unconfirmed';
    default:
      return 'Not set up';
  }
}

/** One line per license: state code, progress bar, earned/target, standing chip. */
function LicenseStatusLine({
  license,
  memberLicenses,
  now,
}: {
  license: AdminLicenseComplianceDto;
  /** The member's full license list, needed to resolve derived (reciprocity) rows. */
  memberLicenses?: AdminLicenseComplianceDto[];
  /** Shared report clock so this line agrees with the band and sort order. */
  now?: Date;
}) {
  const standing = getLicenseStanding(license, now, memberLicenses);
  const atRisk = standing === 'in_progress' && isAdminLicenseAtRisk(license, now, memberLicenses);
  const setUp = standing !== 'not_set_up';
  // Derived (reciprocity) row: no local hours to bar-chart — the standing chip
  // (mirrored from the member's primary license) plus a mode note carries it.
  const derived = license.complianceMode === 'home_state_reciprocity';
  const target = license.targetHours ?? 0;
  const earned = license.earnedHours ?? 0;
  const pct = target > 0 ? Math.min(100, (earned / target) * 100) : 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: LICENSE_LINE_HEIGHT }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: 'text.secondary', width: 24, flexShrink: 0 }}
      >
        {license.stateCode}
      </Typography>
      {setUp && derived ? (
        <>
          <Typography
            variant="caption"
            // Spans the bar (140) + row gap (1.25 spacing = 10px) + hours column
            // (52) so the standing chip lines up with the non-derived rows.
            sx={{ color: 'text.secondary', width: 140 + 10 + 52, flexShrink: 0 }}
          >
            Via home-state reciprocity
          </Typography>
          <Chip
            label={standing === 'concluded' ? 'Compliant' : 'In progress'}
            size="small"
            sx={{
              height: 20,
              fontSize: '11px',
              fontWeight: 500,
              bgcolor: `status.${standing === 'concluded' ? 'completed' : 'onPace'}.bg`,
              color: `status.${standing === 'concluded' ? 'completed' : 'onPace'}.fg`,
            }}
          />
        </>
      ) : setUp ? (
        <>
          {/* The bar encodes amount only, always brand green; standing
              (Compliant / In progress / Attention) is carried by the adjacent
              chip, where blue = still earning and green = done. The explicit
              soft-tint track keeps bar-vs-track contrast ≥3:1 (MUI's default
              lightened track sits under it). */}
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              width: 140,
              height: 6,
              borderRadius: '999px',
              flexShrink: 0,
              bgcolor: 'tint.primarySoft',
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', width: 52, flexShrink: 0 }}
          >
            {Number.isInteger(earned) ? earned : earned.toFixed(1)}/{target}
          </Typography>
          {standing === 'attention' ? (
            <Chip
              label="Attention required"
              size="small"
              sx={{
                height: 20,
                fontSize: '11px',
                fontWeight: 500,
                bgcolor: 'status.attention.bg',
                color: 'status.attention.fg',
              }}
            />
          ) : atRisk ? (
            <Tooltip
              title="Projected to fall short of this period's CPE requirement at the current pace."
              arrow
            >
              <Chip
                label="At risk"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 500,
                  bgcolor: 'status.atRisk.bg',
                  color: 'status.atRisk.fg',
                }}
              />
            </Tooltip>
          ) : (
            <Chip
              label={standing === 'concluded' ? 'Compliant' : 'In progress'}
              size="small"
              sx={{
                height: 20,
                fontSize: '11px',
                fontWeight: 500,
                bgcolor: `status.${standing === 'concluded' ? 'completed' : 'onPace'}.bg`,
                color: `status.${standing === 'concluded' ? 'completed' : 'onPace'}.fg`,
              }}
            />
          )}
        </>
      ) : (
        <ProvenanceBadge status={license.status} stateCode={license.stateCode} />
      )}
    </Box>
  );
}

/**
 * Mobile (<600px) replacement for a table row: whole card is one tap target
 * that opens the member drawer. Standing collapses to the member's bucket chip
 * (per-license detail lives in the drawer); each license gets one summary line.
 */
function MobileMemberCard({
  user,
  bucket,
  now,
  onOpen,
}: {
  user: UserComplianceSummaryDto;
  bucket: MemberBucket;
  /** Shared report clock so license lines agree with the bucket chip. */
  now?: Date;
  onOpen: () => void;
}) {
  const token = BUCKET_TOKENS[bucket];
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        px: 2.5,
        py: 1.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        transition: 'background-color 0.15s ease-out',
        '&:hover': { bgcolor: 'tint.neutralSoft' },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }} noWrap>
            {user.fullName}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} noWrap>
            {user.email}
          </Typography>
        </Box>
        <Chip
          label={BUCKET_LABELS[bucket]}
          size="small"
          sx={{
            height: 20,
            fontSize: '11px',
            fontWeight: 500,
            flexShrink: 0,
            bgcolor: `status.${token}.bg`,
            color: `status.${token}.fg`,
          }}
        />
        <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
      </Box>
      {(user.licenses ?? []).length > 0 && (
        <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {user.licenses.map((license) => (
            <MobileLicenseLine key={license.licenseId} license={license} now={now} />
          ))}
        </Box>
      )}
    </ButtonBase>
  );
}

/** Compact one-line license summary for the mobile card: state, hours, days left. */
function MobileLicenseLine({ license, now }: { license: AdminLicenseComplianceDto; now?: Date }) {
  if (!isLicenseSetUp(license)) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {license.stateCode} · {license.status === 'unconfirmed' ? 'Unconfirmed' : 'Not set up'}
      </Typography>
    );
  }
  // Derived (reciprocity) rows have no local hours or deadline to summarize.
  if (license.complianceMode === 'home_state_reciprocity') {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {license.stateCode} · Via home-state reciprocity
      </Typography>
    );
  }
  const earned = license.earnedHours ?? 0;
  const hours = `${Number.isInteger(earned) ? earned : earned.toFixed(1)}/${license.targetHours ?? 0} hrs`;
  const urgency = license.periodEnd
    ? getUrgency(license.periodEnd, isLicenseCompliant(license), now)
    : null;
  return (
    <Typography
      variant="caption"
      sx={{
        color: urgency?.attentionRequired ? 'status.attention.fg' : 'text.secondary',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {license.stateCode} · {hours}
      {urgency
        ? ` · ${urgency.periodEnded ? 'Period ended' : `${urgency.daysLeft} ${urgency.daysLeft === 1 ? 'day' : 'days'} left`}`
        : ''}
    </Typography>
  );
}

/** One line per license: urgency dot + days remaining in the license's period. */
function TimeToReportLine({
  license,
  memberLicenses,
  now,
}: {
  license: AdminLicenseComplianceDto;
  /** The member's full license list, needed to resolve derived (reciprocity) rows. */
  memberLicenses?: AdminLicenseComplianceDto[];
  now?: Date;
}) {
  const setUp = isLicenseSetUp(license);
  if (!setUp || !license.periodEnd) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: LICENSE_LINE_HEIGHT }}>
        <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>
      </Box>
    );
  }
  const { daysLeft, attentionRequired, periodEnded } = getUrgency(
    license.periodEnd,
    isLicenseCompliant(license, memberLicenses),
    now
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: LICENSE_LINE_HEIGHT }}>
      {/* On-pace time remaining uses the blue on-pace dot, matching the
          standing chips; the neutral enrolled dot stays reserved for
          "not set up" elsewhere on the page. */}
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: attentionRequired ? 'status.attention.dot' : 'status.onPace.dot',
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: attentionRequired ? 'status.attention.fg' : 'text.secondary',
          fontWeight: attentionRequired ? 600 : 400,
        }}
      >
        {periodEnded ? 'Period ended' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
      </Typography>
    </Box>
  );
}
